import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import type { GECRoomStatus } from './GenderEqualityCuisine';

interface Props {
  roomId: string;
  status: GECRoomStatus;
  isAdmin: boolean;
  myUserId: string;
  onEndGame: () => void;
  endingGame: boolean;
  refreshStatus: () => void;
}

interface Dish {
  userId: string;
  userAlias: string;
  dishName: string;
  allergy: string;
  identifyingDetails: string;
  description: string;
}

const MAX_TOKENS = 5;

function VotingStage({ roomId, status, isAdmin, myUserId, onEndGame, endingGame, refreshStatus }: Props) {
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [showRolePicker, setShowRolePicker] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [dishRes, voteRes] = await Promise.all([
        api.gecGetDishes(roomId),
        api.gecGetMyVote(roomId),
      ]);
      if (dishRes.success) {
        setDishes(dishRes.data.dishes || []);
      }
      if (voteRes.success && voteRes.data.votes) {
        setAllocations(voteRes.data.votes);
      }
      setLoaded(true);
    };
    load();
  }, [roomId]);

  const totalUsed = Object.values(allocations).reduce((a, b) => a + b, 0);
  const tokensLeft = MAX_TOKENS - totalUsed;

  const adjust = (chefId: string, delta: number) => {
    if (chefId === myUserId) return;
    const current = allocations[chefId] || 0;
    const next = current + delta;
    if (next < 0) return;
    if (delta > 0 && tokensLeft <= 0) return;
    setAllocations({ ...allocations, [chefId]: next });
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    const result = await api.gecSubmitVotes(roomId, allocations);
    setSubmitting(false);
    if (result.success) {
      refreshStatus();
    } else {
      setError(result.error || 'Failed to submit votes');
    }
  };

  const handlePickRole = async (role: 'chef' | 'voter') => {
    const result = await api.gecSetRole(roomId, role);
    if (result.success) {
      setShowRolePicker(false);
      refreshStatus();
    } else {
      alert(result.error);
    }
  };

  if (!loaded) {
    return (
      <div className="game-content gec-content">
        <div className="loading">🍴 Loading dishes…</div>
      </div>
    );
  }

  // Late joiner without a role yet: prompt them.
  if (!status.currentUserRole) {
    return (
      <div className="game-content gec-content">
        <div className="gec-info-box">
          You joined after voting started! Pick your role to continue.
        </div>
        <div className="gec-role-picker">
          <button className="gec-role-card" onClick={() => handlePickRole('chef')}>
            <div className="gec-role-emoji">🧑‍🍳</div>
            <div className="gec-role-label">Chef</div>
            <div className="gec-role-hint">I'll submit a dish</div>
          </button>
          <button className="gec-role-card" onClick={() => handlePickRole('voter')}>
            <div className="gec-role-emoji">🗳️</div>
            <div className="gec-role-label">Voter</div>
            <div className="gec-role-hint">I'm just here to vote</div>
          </button>
        </div>
      </div>
    );
  }

  // Chef who joined late and hasn't submitted a dish yet
  if (status.currentUserRole === 'chef' && !status.currentUserDishSubmitted) {
    return (
      <div className="game-content gec-content">
        <div className="gec-info-box">
          You're a chef who joined late! Go to the waiting screen to submit your dish — refresh after submitting.
        </div>
      </div>
    );
  }

  const alreadyVoted = status.currentUserVoted;

  return (
    <div className="game-content gec-content">
      <div className="gec-token-bar">
        <div className="gec-token-label">
          🎫 Tokens left
        </div>
        <div className={`gec-token-count ${tokensLeft === 0 ? 'empty' : ''}`}>
          {tokensLeft} / {MAX_TOKENS}
        </div>
      </div>

      <div className="gec-status-line">
        🗳️ {status.votedCount} / {status.totalVoters} voted
        {alreadyVoted && <span className="gec-voted-pill"> · Your votes are locked in ✅</span>}
      </div>

      {dishes.length === 0 && (
        <div className="gec-info-box">No dishes have been submitted yet.</div>
      )}

      <div className="gec-dish-list">
        {dishes.map((dish) => {
          const allocated = allocations[dish.userId] || 0;
          const isMyDish = dish.userId === myUserId;
          return (
            <details key={dish.userId} className={`gec-dish-card ${isMyDish ? 'mine' : ''}`}>
              <summary>
                <div className="gec-dish-summary">
                  <div className="gec-dish-name">
                    🍽️ {dish.dishName}
                    {isMyDish && <span className="gec-mine-badge">Your dish</span>}
                  </div>
                  <div className="gec-vote-controls">
                    <button
                      type="button"
                      className="gec-vote-btn minus"
                      disabled={isMyDish || allocated === 0 || alreadyVoted}
                      onClick={(e) => { e.preventDefault(); adjust(dish.userId, -1); }}
                      aria-label="Remove token"
                    >
                      −
                    </button>
                    <span className="gec-allocated">{allocated}</span>
                    <button
                      type="button"
                      className="gec-vote-btn plus"
                      disabled={isMyDish || tokensLeft <= 0 || alreadyVoted}
                      onClick={(e) => { e.preventDefault(); adjust(dish.userId, 1); }}
                      aria-label="Add token"
                    >
                      +
                    </button>
                  </div>
                </div>
              </summary>
              <div className="gec-dish-body">
                {dish.allergy && (
                  <div className="gec-dish-field"><strong>⚠️ Allergies:</strong> {dish.allergy}</div>
                )}
                <div className="gec-dish-field"><strong>🔎 Identifying details:</strong> {dish.identifyingDetails}</div>
                <div className="gec-dish-field"><strong>📝 Description:</strong> {dish.description}</div>
              </div>
            </details>
          );
        })}
      </div>

      {error && <div className="gec-error">{error}</div>}

      {!alreadyVoted && dishes.length > 0 && (
        <button
          className="gec-primary-btn"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Submitting…' : `Submit My Votes (${totalUsed} used)`}
        </button>
      )}

      {isAdmin && (
        <div className="gec-admin-actions">
          <p className="gec-admin-hint">As admin, you can end voting and reveal results whenever you're ready.</p>
          <button
            className="gec-danger-btn"
            disabled={endingGame}
            onClick={onEndGame}
          >
            {endingGame ? 'Ending…' : 'End Voting & Reveal Results'}
          </button>
        </div>
      )}
    </div>
  );
}

export default VotingStage;
